/**
 * ClassesFilters - Filter component for classes page
 *
 * @example
 * ```tsx
 * <ClassesFilters
 *   seasonOptions={seasons}
 *   locationOptions={locations}
 *   teacherOptions={teachers}
 *   seasonFilter={season}
 *   locationFilter={location}
 *   teacherFilter={teacher}
 *   selectedDays={days}
 *   onSeasonChange={setSeason}
 *   onLocationChange={setLocation}
 *   onTeacherChange={setTeacher}
 *   onDaysChange={setDays}
 * />
 * ```
 */

"use client";

import { Flex } from "@chakra-ui/react";
import {
  CustomSelect,
  CustomSelectMultiple,
  type CustomSelectOption,
} from "@/components/shared/formElements/CustomSelect";
import { DAY_OPTIONS } from "@/lib/constants/classes";

interface ClassesFiltersProps {
  seasonOptions: CustomSelectOption[];
  locationOptions: string[];
  teacherOptions: CustomSelectOption[];
  seasonFilter: string | null;
  locationFilter: string | null;
  teacherFilter: string | null;
  selectedDays: string[];
  onSeasonChange: (val: string | null) => void;
  onLocationChange: (val: string | null) => void;
  onTeacherChange: (val: string | null) => void;
  onDaysChange: (days: string[]) => void;
}

/** 768–1034px: fixed 145px controls in a 2×2 wrap. ≥1035px: natural width in one row. */
const FILTER_CONTROL_WIDTH = {
  base: "100%",
  md: "145px",
} as const;

const filterControlProps = {
  w: FILTER_CONTROL_WIDTH,
  minW: FILTER_CONTROL_WIDTH,
  maxW: FILTER_CONTROL_WIDTH,
  sx: {
    "@media (min-width: 1035px)": {
      width: "max-content",
      minWidth: "max-content",
      maxWidth: "max-content",
    },
  },
} as const;

export const ClassesFilters = ({
  seasonOptions,
  locationOptions,
  teacherOptions,
  seasonFilter,
  locationFilter,
  teacherFilter,
  selectedDays,
  onSeasonChange,
  onLocationChange,
  onTeacherChange,
  onDaysChange,
}: ClassesFiltersProps) => {
  return (
    <Flex
      gap="10px"
      wrap="wrap"
      w={{ base: "100%", md: "max-content" }}
      maxW={{ base: "100%", md: "300px" }}
      direction={{ base: "column", md: "row" }}
      sx={{
        "@media (min-width: 1035px)": {
          maxWidth: "none",
        },
      }}
    >
      {seasonOptions.length > 1 && (
        <CustomSelect
          placeholder="All Seasons"
          formControlProps={filterControlProps}
          options={[
            { label: "All Seasons", value: "" },
            ...seasonOptions,
          ]}
          value={seasonFilter ?? ""}
          onChange={(val) => onSeasonChange(val === "" ? null : val)}
        />
      )}

      {locationOptions.length > 1 && (
        <CustomSelect
          placeholder="All Locations"
          formControlProps={filterControlProps}
          options={[
            { label: "All Locations", value: "" },
            ...locationOptions.map((location) => ({
              label: location,
              value: location,
            })),
          ]}
          value={locationFilter ?? ""}
          onChange={(val) => onLocationChange(val === "" ? null : val)}
        />
      )}

      {teacherOptions.length > 1 && (
        <CustomSelect
          placeholder="All Instructors"
          formControlProps={filterControlProps}
          options={[
            { label: "All Instructors", value: "" },
            ...teacherOptions,
          ]}
          value={teacherFilter ?? ""}
          onChange={(val) => onTeacherChange(val === "" ? null : val)}
        />
      )}

      <CustomSelectMultiple
        options={[...DAY_OPTIONS]}
        selected={selectedDays}
        placeholder="Select days"
        onChange={onDaysChange}
        dropdownHeight="max-content"
        formControlProps={filterControlProps}
      />
    </Flex>
  );
};
